import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { fmtRs } from "../../utils/payrollFormat";

const PRIMARY = "#1B5C20";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Payroll Summary
 *
 * Uses a clean statement-style layout instead of individual cards.
 * Net Salary receives a subtle green highlight.
 */
function PayrollSummary({ payroll }) {
  const items = [
    {
      label: "Gross Earnings",
      value: payroll.gross,
    },
    {
      label: "Total Earnings to Gross",
      value: payroll.totalEarnings,
    },
    {
      label: "Total Deductions from Gross",
      value: payroll.totalDeductions,
    },
    {
      label: "Employee EPF (8%)",
      value: payroll.epf,
    },
    {
      label: "Employer EPF (12%)",
      value: payroll.employerEpf,
    },
    {
      label: "Employer ETF (3%)",
      value: payroll.employerEtf,
    },
  ];

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          mb: 1.5,
          color: "#222",
        }}
      >
        Payroll Summary
      </Typography>

      <Box
        sx={{
          border: "1px solid #EAEAEA",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        {/* Summary Items */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
          }}
        >
          {items.map((item, index) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,

                borderBottom: "1px solid #EAEAEA",

                borderRight: {
                  xs: "none",
                  sm:
                    index % 2 === 0
                      ? "1px solid #EAEAEA"
                      : "none",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#777",
                }}
              >
                {item.label}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#222",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {fmtRs(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Net Salary */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1.75,
            backgroundColor: "#F0F7F0",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: PRIMARY,
            }}
          >
            Net Salary
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontWeight: 800,
              color: PRIMARY,
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            {fmtRs(payroll.net)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function DetailTable({ title, rows, type }) {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#222",
          }}
        >
          {title}
        </Typography>

        <Chip
          label={`${rows.length} item${
            rows.length !== 1 ? "s" : ""
          }`}
          size="small"
          sx={{
            backgroundColor:
              type === "earning"
                ? "#E8F5E9"
                : "#FFF3E0",
            color:
              type === "earning"
                ? PRIMARY
                : "#B45309",
            fontWeight: 600,
          }}
        />
      </Box>

      <Box
        sx={{
          border: "1px solid #EAEAEA",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "100px 1fr 1.5fr 120px",
            gap: 1,
            px: 2,
            py: 1.25,
            backgroundColor: "#1f1f1f",
            color: "#34d399",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700 }}
          >
            CODE
          </Typography>

          <Typography
            variant="caption"
            sx={{ fontWeight: 700 }}
          >
            COMPONENT
          </Typography>

          <Typography
            variant="caption"
            sx={{ fontWeight: 700 }}
          >
            DESCRIPTION
          </Typography>

          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textAlign: "right",
            }}
          >
            AMOUNT
          </Typography>
        </Box>

        {/* Rows */}
        {rows.map((row, index) => (
          <Box
            key={`${row.componentId}-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns:
                "100px 1fr 1.5fr 120px",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: "1px solid #EAEAEA",
              backgroundColor:
                index % 2 === 0
                  ? "#fff"
                  : "#FAFAFA",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#555",
              }}
            >
              {row.componentCode || "-"}
            </Typography>

            <Typography variant="body2">
              {row.componentName || "-"}
            </Typography>

            <Typography
              variant="body2"
              color="#666"
            >
              {row.description || "-"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                textAlign: "right",
              }}
            >
              {fmtRs(row.amount)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PayrollBreakdownDialog({
  open,
  onClose,
  payroll,
}) {
  if (!payroll) {
    return null;
  }

  const details = payroll.details || [];

  const earnings = details.filter(
    (item) =>
      String(item.componentType || "").toUpperCase() ===
      "E"
  );

  const deductions = details.filter(
    (item) =>
      String(item.componentType || "").toUpperCase() ===
      "D"
  );

  const other = details.filter((item) => {
    const type = String(
      item.componentType || ""
    ).toUpperCase();

    return type !== "E" && type !== "D";
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#222",
              }}
            >
              Payroll Breakdown
            </Typography>

            <Typography
              variant="body2"
              color="#666"
              sx={{ mt: 0.5 }}
            >
              {payroll.name || "-"}{" "}
              {payroll.empNo
                ? `(${payroll.empNo})`
                : ""}
            </Typography>
          </Box>

          <Button
            onClick={onClose}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: 2,
              color: "#666",
            }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: "#FAFAFA",
        }}
      >
        {/* Employee / Payroll Information */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="#888"
            >
              Employee
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
            >
              {payroll.name || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="#888"
            >
              Service No
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
            >
              {payroll.empNo || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="#888"
            >
              Payroll No
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
            >
              {payroll.payrollNo || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="#888"
            >
              Pay Date
            </Typography>

            <Typography
              variant="body2"
              fontWeight={600}
            >
              {formatDate(payroll.payDate)}
            </Typography>
          </Box>
        </Box>

        {/* Attendance Information */}
        <Box
          sx={{
            border: "1px solid #EAEAEA",
            borderRadius: 2,
            backgroundColor: "#fff",
            p: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            Attendance & Payroll Days
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="#888"
              >
                Working Days
              </Typography>

              <Typography fontWeight={600}>
                {payroll.workingDays}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="#888"
              >
                Present Days
              </Typography>

              <Typography fontWeight={600}>
                {payroll.presentDays}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="#888"
              >
                No Pay Days
              </Typography>

              <Typography fontWeight={600}>
                {payroll.noPayDays}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="#888"
              >
                OT Hours
              </Typography>

              <Typography fontWeight={600}>
                {payroll.otHours}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payroll Summary */}
        <PayrollSummary payroll={payroll} />

        <Divider sx={{ mt: 3 }} />

        {/* Component Details */}
        <DetailTable
          title="Earnings"
          rows={earnings}
          type="earning"
        />

        <DetailTable
          title="Deductions"
          rows={deductions}
          type="deduction"
        />

        {other.length > 0 && (
          <DetailTable
            title="Other Components"
            rows={other}
            type="earning"
          />
        )}

        {/* Empty Details */}
        {details.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 5,
            }}
          >
            <Typography
              variant="body2"
              color="#777"
            >
              No component breakdown is available
              for this payroll.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: PRIMARY,
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            "&:hover": {
              backgroundColor: "#145016",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}